/**
 * VOSection Component
 * Section for displaying and managing variation orders within PaymentMilestonesSection
 */

import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import AddVOModal from './AddVOModal';
import RecordVOPaymentModal from './RecordVOPaymentModal';
import EditVOModal from './EditVOModal';
import EditVOPaymentModal from './EditVOPaymentModal';
import VoidVOModal from './VoidVOModal';

const VOSection = ({ projectId, summary, onRefresh }) => {
  const [showAddVO, setShowAddVO] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showEditVO, setShowEditVO] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [showVoidVO, setShowVoidVO] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [selectedVO, setSelectedVO] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedVOForPayment, setSelectedVOForPayment] = useState(null);
  const [expandedVOs, setExpandedVOs] = useState({});

  const vos = summary?.vos || [];
  const voidedVOs = summary?.voidedVOs || [];
  const voCount = summary?.voCount || 0;
  const voTotalAmount = summary?.voTotalAmount || 0;
  const voTotalPaid = summary?.voTotalPaid || 0;

  // Toggle VO expansion
  const toggleVOExpand = (voId) => {
    setExpandedVOs(prev => ({
      ...prev,
      [voId]: !prev[voId]
    }));
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle record payment
  const handleRecordPayment = (vo) => {
    setSelectedVO(vo);
    setShowRecordPayment(true);
  };

  // Handle edit VO
  const handleEditVO = (vo) => {
    setSelectedVO(vo);
    setShowEditVO(true);
  };

  // Handle void VO
  const handleVoidVO = (vo) => {
    setVoidTarget(vo);
    setShowVoidVO(true);
  };

  // Handle edit payment
  const handleEditPayment = (payment, vo) => {
    setSelectedPayment(payment);
    setSelectedVOForPayment(vo);
    setShowEditPayment(true);
  };

  // Handle modal success
  const handleModalSuccess = () => {
    setShowAddVO(false);
    setShowRecordPayment(false);
    setShowEditVO(false);
    setShowEditPayment(false);
    setShowVoidVO(false);
    setVoidTarget(null);
    setSelectedVO(null);
    setSelectedPayment(null);
    setSelectedVOForPayment(null);
    onRefresh();
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-MY', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format payment method
  const formatPaymentMethod = (method) => {
    if (!method) return '-';
    return method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-gray-900">Variation Orders</h4>
          {voCount > 0 && (
            <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-0.5 rounded-full">
              {voCount}
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAddVO(true)}
          className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700 font-medium min-h-[44px] px-2 touch-manipulation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add VO
        </button>
      </div>

      {/* Summary Stats */}
      {voCount > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-purple-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Total VO</p>
            <p className="font-semibold text-purple-700 text-sm">{formatCurrency(voTotalAmount)}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Paid</p>
            <p className="font-semibold text-green-700 text-sm">{formatCurrency(voTotalPaid)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 text-center">
            <p className="text-xs text-gray-500">Remaining</p>
            <p className="font-semibold text-gray-700 text-sm">{formatCurrency(voTotalAmount - voTotalPaid)}</p>
          </div>
        </div>
      )}

      {/* VO List */}
      {voCount === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-500">No variation orders yet</p>
          <button
            onClick={() => setShowAddVO(true)}
            className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            Add first VO
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {vos.map((vo) => {
            const isExpanded = expandedVOs[vo.id];
            const percentPaid = vo.amount > 0 ? (vo.actualPaidAmount / vo.amount * 100) : 0;

            return (
              <div
                key={vo.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* VO Header - Clickable */}
                <div
                  onClick={() => toggleVOExpand(vo.id)}
                  className="p-3 bg-white hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-purple-700">{vo.voNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(vo.status)}`}>
                          {vo.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">{vo.description}</p>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(vo.amount)}</p>
                      <p className="text-xs text-gray-500">
                        Paid: {formatCurrency(vo.actualPaidAmount)}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full transition-all ${
                          vo.status === 'completed' ? 'bg-green-500' :
                          vo.status === 'partial' ? 'bg-yellow-500' : 'bg-gray-300'
                        }`}
                        style={{ width: `${Math.min(percentPaid, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Expand indicator */}
                  <div className="flex justify-center mt-2">
                    <svg
                      className={`w-4 h-4 text-gray-400 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-3">
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <button
                        onClick={() => handleRecordPayment(vo)}
                        className="flex-1 min-w-[140px] px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 min-h-[44px] touch-manipulation"
                      >
                        Record Payment
                      </button>
                      <button
                        onClick={() => handleEditVO(vo)}
                        className="px-3 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 min-h-[44px] touch-manipulation"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleVoidVO(vo)}
                        className="px-3 py-2 border border-red-300 bg-white text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 min-h-[44px] touch-manipulation"
                      >
                        Void
                      </button>
                    </div>

                    {/* Files */}
                    {(vo.quotationFiles?.length > 0 || vo.invoiceFiles?.length > 0) && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Files</p>
                        <div className="flex flex-wrap gap-2">
                          {vo.quotationFiles?.map((file, idx) => (
                            <a
                              key={`q-${idx}`}
                              href={file.filePath || `/api/project-files/${file.fileId}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-purple-600 hover:text-purple-700 bg-purple-50 px-2 py-1 rounded"
                            >
                              Quotation {idx + 1}
                            </a>
                          ))}
                          {vo.invoiceFiles?.map((file, idx) => (
                            <a
                              key={`i-${idx}`}
                              href={file.filePath || `/api/project-files/${file.fileId}/download`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-purple-600 hover:text-purple-700 bg-purple-50 px-2 py-1 rounded"
                            >
                              Invoice {idx + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment History */}
                    {vo.paymentRecords?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Payment History</p>
                        <div className="space-y-2">
                          {vo.paymentRecords.map((payment) => (
                            <div
                              key={payment.id}
                              className="bg-white rounded-lg p-2 border border-gray-100"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-green-600">
                                    {formatCurrency(payment.amount)}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatDate(payment.paymentDate)} • {formatPaymentMethod(payment.paymentMethod)}
                                  </p>
                                  {payment.referenceNumber && (
                                    <p className="text-xs text-gray-400">Ref: {payment.referenceNumber}</p>
                                  )}
                                </div>
                                <div className="flex items-start gap-2">
                                  {payment.recorder && (
                                    <p className="text-xs text-gray-400">by {payment.recorder.name}</p>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditPayment(payment, vo);
                                    }}
                                    className="p-1 text-gray-400 hover:text-blue-600 touch-manipulation"
                                    title="Edit payment"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              {payment.notes && (
                                <p className="text-xs text-gray-500 mt-1 italic">{payment.notes}</p>
                              )}
                              {payment.receiptFiles?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {payment.receiptFiles.map((file, idx) => (
                                    <a
                                      key={idx}
                                      href={file.filePath || `/api/project-files/${file.fileId}/download`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:text-blue-700"
                                    >
                                      Receipt {idx + 1}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {vo.paymentRecords?.length === 0 && (
                      <p className="text-xs text-gray-400 text-center py-2">No payments recorded yet</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Voided VOs (history only, collapsed by default via details tag) */}
      {voidedVOs.length > 0 && (
        <details className="mt-4 border border-gray-200 rounded-lg bg-gray-50">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-600">
            Voided VOs ({voidedVOs.length})
          </summary>
          <div className="p-3 space-y-2">
            {voidedVOs.map(vo => (
              <div key={vo.id} className="bg-white border border-gray-200 rounded-lg p-3 opacity-80">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-medium text-gray-500 line-through">{vo.voNumber}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Voided</span>
                    </div>
                    <p className="text-sm text-gray-600 line-through line-clamp-2">{vo.description}</p>
                    {vo.voidReason && (
                      <p className="text-xs text-gray-500 mt-1">Reason: {vo.voidReason}</p>
                    )}
                    {vo.voidedAt && (
                      <p className="text-xs text-gray-400 mt-1">Voided {formatDate(vo.voidedAt)}</p>
                    )}
                  </div>
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-500 line-through">{formatCurrency(vo.amount)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Modals */}
      <AddVOModal
        isOpen={showAddVO}
        onClose={() => setShowAddVO(false)}
        onSuccess={handleModalSuccess}
        projectId={projectId}
      />

      {selectedVO && (
        <>
          <RecordVOPaymentModal
            isOpen={showRecordPayment}
            onClose={() => {
              setShowRecordPayment(false);
              setSelectedVO(null);
            }}
            onSuccess={handleModalSuccess}
            vo={selectedVO}
            projectId={projectId}
          />

          <EditVOModal
            isOpen={showEditVO}
            onClose={() => {
              setShowEditVO(false);
              setSelectedVO(null);
            }}
            onSuccess={handleModalSuccess}
            vo={selectedVO}
            projectId={projectId}
          />
        </>
      )}

      {showEditPayment && selectedPayment && selectedVOForPayment && (
        <EditVOPaymentModal
          isOpen={showEditPayment}
          onClose={() => {
            setShowEditPayment(false);
            setSelectedPayment(null);
            setSelectedVOForPayment(null);
          }}
          onSuccess={handleModalSuccess}
          payment={selectedPayment}
          vo={selectedVOForPayment}
          projectId={projectId}
        />
      )}

      {showVoidVO && voidTarget && (
        <VoidVOModal
          isOpen={showVoidVO}
          onClose={() => {
            setShowVoidVO(false);
            setVoidTarget(null);
          }}
          onSuccess={handleModalSuccess}
          vo={voidTarget}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default VOSection;
